import { ConfirmationAction } from '@app/components/ConfirmationAction';
import { SocialPlatform } from '@app/modules/social-export/domain/platforms';
import {
  SocialExportListConnectionsQueryParamsPlatformEnumKey,
  socialExportListConnectionsQueryKey,
  useSocialExportDeleteConnection,
} from '@mi-front/api-v2';
import { LinkOff } from '@mui/icons-material';
import { Button, CircularProgress } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { FormattedMessage, useIntl } from 'react-intl';
import { connectionsManageMessages as messages } from './connections-manage-messages';

type Props = {
  connectionId: string;
  platform: SocialPlatform;
  linkedStationsCount: number;
};

/**
 * Disconnects a client-owned connection by id, warning when stations are still linked.
 */
export function PlatformDisconnect({ connectionId, platform, linkedStationsCount }: Props) {
  const { $t } = useIntl();
  const queryClient = useQueryClient();
  const mutation = useSocialExportDeleteConnection({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: socialExportListConnectionsQueryKey({
            platform: platform.id as SocialExportListConnectionsQueryParamsPlatformEnumKey,
          }),
        });
      },
    },
  });

  const message =
    linkedStationsCount > 0
      ? $t(messages.disconnectWithStationsWarning, { count: linkedStationsCount })
      : $t(messages.disconnectConfirmDescription);

  return (
    <ConfirmationAction
      title={$t(messages.disconnectConfirmTitle)}
      message={message}
      confirmColor="error"
      onConfirm={() => mutation.mutateAsync({ id: connectionId })}
    >
      {({ onClick }) => (
        <Button
          color="error"
          size="small"
          variant="outlined"
          startIcon={
            mutation.isPending ? <CircularProgress size={16} color="inherit" /> : <LinkOff />
          }
          disabled={mutation.isPending}
          onClick={onClick}
        >
          <FormattedMessage {...messages.disconnect} />
        </Button>
      )}
    </ConfirmationAction>
  );
}
